import React, { useEffect, useRef, useState } from 'react';
import { PALETTES, paletteFor } from '../../utils/agentLook';
import { hasPointer, trackPointer } from '../../utils/eyeTracker';
import './agentDot.css';

const reduce = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Avatar de un agente: un personaje con ojos que respira, parpadea y mira al cursor.
 * state: idle | thinking | talking | happy | shy | sleep (sleep = archivado o apagado).
 * Color: `colors` {a, b} (el que eligió el admin) o `palette` o, si no hay, el que sale del nombre (igual que en el widget del chat web).
 * shape: dot | square | ears | antenna. imageUrl: imagen propia en lugar del personaje (sin ojos).
 */
export default function AgentDot({ name = '', palette, colors, shape = 'dot', imageUrl, size = 40, state = 'idle', className = '', label, follow = true }) {
  const ref = useRef(null);
  const p = colors || PALETTES[palette] || PALETTES[paletteFor(name)];
  const [imgOk, setImgOk] = useState(false);

  useEffect(() => {
    setImgOk(false);
    if (!imageUrl) return undefined;
    const im = new Image();
    im.referrerPolicy = 'no-referrer';
    im.onload = () => setImgOk(true);
    im.src = imageUrl;
    return () => { im.onload = null; };
  }, [imageUrl]);

  // Parpadeo, y miradas al azar mientras no se haya movido el cursor
  useEffect(() => {
    if (reduce() || state === 'sleep') return undefined;
    const el = ref.current;
    let blink; let look; let back;
    const doBlink = () => { blink = setTimeout(() => { el?.classList.remove('is-blink'); void el?.offsetWidth; el?.classList.add('is-blink'); doBlink(); }, 2600 + Math.random() * 3400); };
    const doLook = () => {
      look = setTimeout(() => {
        const eyes = el?.querySelector('.adot-eyes');
        if (eyes && el.getAttribute('data-state') === 'idle' && !(follow && hasPointer())) {
          eyes.style.setProperty('--ex', `${(Math.random() * 6 - 3).toFixed(1)}px`);
          eyes.style.setProperty('--ey', `${(Math.random() * 3 - 1.5).toFixed(1)}px`);
          back = setTimeout(() => { eyes.style.removeProperty('--ex'); eyes.style.removeProperty('--ey'); }, 1400);
        }
        doLook();
      }, 4000 + Math.random() * 4000);
    };
    doBlink(); doLook();
    return () => { clearTimeout(blink); clearTimeout(look); clearTimeout(back); };
  }, [state, follow]);

  // Los ojos siguen al cursor
  useEffect(() => {
    const el = ref.current;
    if (!follow || reduce() || !el || state === 'sleep' || state === 'thinking' || state === 'shy' || (imageUrl && imgOk)) return undefined;
    const eyes = el.querySelector('.adot-eyes');
    const off = trackPointer((ptr) => {
      if (!ptr) { eyes.style.removeProperty('--ex'); eyes.style.removeProperty('--ey'); el.classList.remove('is-track'); return; }
      const r = el.getBoundingClientRect();
      if (!r.width || r.bottom < 0 || r.top > window.innerHeight) return;
      const dx = ptr.x - (r.left + r.width / 2); const dy = ptr.y - (r.top + r.height / 2);
      const dist = Math.hypot(dx, dy) || 1; const k = Math.min(1, dist / 140);
      el.classList.add('is-track');
      eyes.style.setProperty('--ex', `${((dx / dist) * k * r.width * 0.1).toFixed(2)}px`);
      eyes.style.setProperty('--ey', `${((dy / dist) * k * r.height * 0.075).toFixed(2)}px`);
    });
    return () => { off(); eyes.style.removeProperty('--ex'); eyes.style.removeProperty('--ey'); el.classList.remove('is-track'); };
  }, [follow, state, imageUrl, imgOk]);

  return (
    <div ref={ref} className={`adot ${imgOk ? 'is-img' : ''} ${className}`} data-state={state} data-shape={shape}
      style={{ '--s': `${size}px`, '--a': p.a, '--b': p.b, ...(imgOk ? { '--img': `url("${imageUrl}")` } : {}) }}
      role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <div className="adot-blob" />
      <div className="adot-orbit"><i /><i /></div>
      <div className="adot-eyes"><div className="adot-eye" /><div className="adot-eye" /></div>
    </div>
  );
}
